/**
 * Provides the `addParametersUTM` utility function for adding UTM parameters to a URL string, and its associated `UTMParameters` type.
 * @module addParametersUTM
 */
// Import global utils
import { isEmpty } from '@automator/shared/utils/is-empty';
import { addParametersToURL } from '@automator/shared/utils/add-parameters-to-url';
/**
 * Adds standard UTM tracking parameters to a URL, following the in-plugin UTM Convention.
 *
 * **Behavior (UTM Convention — all values lowercase, hyphenated):**
 * - Returns `''` if the input `url` is empty, null, or undefined.
 * - `utm_source` — `uncanny-automator-pro` when `UncannyAutomatorBackend._site.automator.is_pro_active`
 *   is truthy, otherwise `uncanny-automator`.
 * - `utm_medium` — always `in-plugin` (every link from inside the plugin admin UI).
 * - `utm_content` — the UI location (the `medium` argument). If a `content` argument is also given it is
 *   appended as a variant, producing `location-variant`.
 * - `utm_campaign` — the initiative (the `campaign` argument), when provided.
 * - `utm_r` — `marketing_referer` from the global config, when present.
 *
 * @since 5.0 Initial implementation.
 * @since 7.x Retagged to the UTM Convention: `utm_medium` is fixed to `in-plugin`, the caller `medium`
 *            (UI location) moves to `utm_content`, the source separator is hyphenated, and values are
 *            lowercased / underscores converted to hyphens.
 * @param {string | null | undefined} url - The base URL to which UTM parameters will be appended.
 * @param {UTMParameters} parameters - The UTM inputs.
 * @param {string} [parameters.medium] - The UI location (e.g. `add_token`, `publish_metabox`) -> `utm_content`.
 * @param {string} [parameters.campaign] - The initiative (e.g. `upgrade_to_pro`) -> `utm_campaign`.
 * @param {string} [parameters.content] - Optional variant appended to the location in `utm_content`.
 * @return {string} The resulting URL, or `''` when the input URL is empty/null/undefined.
 *
 * @example
 * ```typescript
 * import { addParametersUTM } from '@automator/shared/utils/add-parameters-utm-to-url';
 * // is_pro_active = false
 * addParametersUTM( 'https://automatorplugin.com/pricing/', { medium: 'add_token', campaign: 'upgrade_to_pro', content: 'requires_user_data' } );
 * // => ".../pricing/?utm_source=uncanny-automator&utm_medium=in-plugin&utm_content=add-token-requires-user-data&utm_campaign=upgrade-to-pro"
 * ```
 */
export function addParametersUTM(url, { medium, campaign, content } // Destructure typed parameters
) {
    // If the base URL is empty, null, or undefined, return it as is.
    if (isEmpty(url)) {
        return url || '';
    }
    // Lowercase + hyphenate a value per the UTM Convention.
    const toUtmValue = (value) => value.toLowerCase().replace(/_/g, '-');
    // Define the parameters we're going to add.
    const urlParameters = {};
    /**
     * `utm_source`
     * Which plugin emitted the link (lowercase, hyphenated).
     */
    urlParameters.utm_source = UncannyAutomatorBackend?._site?.automator?.is_pro_active ? 'uncanny-automator-pro' : 'uncanny-automator';
    /**
     * `utm_medium`
     * Fixed for every in-plugin link (UTM Convention).
     */
    urlParameters.utm_medium = 'in-plugin';
    /**
     * `utm_content`
     * The UI location (the `medium` argument), with an optional appended variant (the `content` argument).
     */
    const contentParts = [];
    if (!isEmpty(medium)) {
        contentParts.push(toUtmValue(medium));
    }
    if (content && !isEmpty(content)) {
        contentParts.push(toUtmValue(content));
    }
    if (contentParts.length) {
        urlParameters.utm_content = contentParts.join('-');
    }
    /**
     * `utm_campaign`
     * The initiative, when provided.
     */
    if (!isEmpty(campaign)) {
        urlParameters.utm_campaign = toUtmValue(campaign);
    }
    /**
     * `utm_r`
     * Check if we have to add the utm_r parameter from global config
     */
    const marketingReferer = UncannyAutomatorBackend?._site?.automator?.links?.marketing_referer;
    if (!isEmpty(marketingReferer)) {
        urlParameters.utm_r = marketingReferer;
    }
    // Add the collected parameters to the URL using the utility function.
    const finalUrl = addParametersToURL(url, urlParameters);
    // Return the modified URL
    return finalUrl;
}
;
